import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import ReactDOM from "react-dom";

import { useSearchParams } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { CollapseGrabber } from "@/components/CollapseGrabber/CollapseGrabber";

import { LoadingOverlay } from "@/components";

import {
	useAddPeopleToTrackerOrganizationMutation,
	useAddProspectToTrackerOrganizationMutation,
	useGetTrackerOrganizationQuery,
	useRemovePersonFromTrackerOrganizationMutation,
} from "@/pages/Private/redux/profileTracker/profileTracker.api";

import { useGetContactProspectDetailQuery } from "@/pages/Private/redux/contact/contact.api";

import { profileTrackerSelector } from "@/pages/Private/redux/profileTracker/profileTracker.slice";
import { useAppSelector } from "@/redux/hooks";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { SignalFilterStatus } from "@/enum/list.enum";

import { GlobalSearchModal } from "@/components/GlobalSearchModal/GlobalSearchModal";

import { ContactLeadLocation } from "@/pages/Private/pages/Contacts/components/ContactLeadLocation";
import { RecentSignalsBox } from "@/pages/Private/pages/Contacts/components/RecentSignalsBox";
import {
	LeadPanelTabItem,
	LeadPanelTabs,
} from "@/pages/Private/pages/Contacts/components/LeadPanelTabs";

import { EmailIconProps } from "@/components/EmailIcon/EmailIcon";
import { getEmailIconStatus } from "@/components/EmailIcon/getEmailIconStatus";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { DeepResearchPanel } from "./DeepResearchPanel";
import { SignalTimeline } from "./SignalTimeline";
import { OverviewTab } from "./OverviewTab";
import { ProfilesTab } from "./ProfilesTab";
import { PersonHeader } from "./PersonHeader";

import { CompanyHeader } from "./CompanyHeader";

import { hasValidDeepResearchLead } from "./deepResearchLead";

import { PersonSearch } from "../../../LiPeople/schema/liPeople";
import { RemovePersonModal } from "./RemovePersonModal";

import { TrackerOrganization } from "../../schema/trackerOrganizations";
import { TrackerPerson } from "../../schema/trackerPerson";

interface DetailViewProps {
	selectedTrackerOrganization?: TrackerOrganization;
	setShowDetailView: (value: boolean) => void;
	signalFilterStatus?: SignalFilterStatus[];
}

enum Tabs {
	OVERVIEW = "OVERVIEW",
	RESEARCH = "RESEARCH",
	TIMELINE = "TIMELINE",
	PROFILES = "PROFILES",
}

export const DetailView: FunctionComponent<DetailViewProps> = ({
	selectedTrackerOrganization,
	setShowDetailView,
	signalFilterStatus,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`profileTracker.${key}`);

	const ref = useRef<HTMLDivElement | null>(null);
	// The panel element, for popovers that must stay inside it: the outside-mousedown close below
	// would otherwise unmount a portaled Sources list before its link's click fires (PREC-4168).
	const [panelEl, setPanelEl] = useState<HTMLDivElement | null>(null);
	const [searchParams, setSearchParams] = useSearchParams();

	const { data: trackerOrganization, isLoading: isLoadingTrackerOrganization } =
		useGetTrackerOrganizationQuery(selectedTrackerOrganization?.id || 0);

	// Get tab from URL or default to OVERVIEW
	const currentTab = (searchParams.get("detailTab") as Tabs) || Tabs.OVERVIEW;

	const setCurrentTab = useCallback(
		(tab: Tabs) => {
			const newParams = new URLSearchParams(searchParams);

			newParams.set("detailTab", tab);
			setSearchParams(newParams, { replace: true });
		},
		[searchParams, setSearchParams]
	);
	const [companyLogoError, setCompanyLogoError] = useState(false);
	const [showSearchPerson, setShowSearchPerson] = useState<boolean>(false);
	const [showRemovePerson, setShowRemovePerson] = useState<boolean>(false);

	const [
		removePersonFromTrackerOrganization,
		{ isLoading: removePersonIsLoading, error: errorRemoving },
	] = useRemovePersonFromTrackerOrganizationMutation();

	const [addProspectToTrackerOrganization, { isLoading: addProspectIsLoading }] =
		useAddProspectToTrackerOrganizationMutation();

	const [selectedTrackerPerson, setSelectedTrackerPerson] = useState<TrackerPerson>();

	// Track which prospect is currently being toggled (for loading indicator)
	const [togglingProspectId, setTogglingProspectId] = useState<number | null>(null);

	// Track failed profile images to avoid DOM manipulation issues
	const [failedProfileImages, setFailedProfileImages] = useState<Set<number>>(new Set());

	const [addPeopleToTrackerOrganization] = useAddPeopleToTrackerOrganizationMutation();

	useEffect(() => {
		/**
		 * Alert if clicked on outside of element
		 */
		function handleClickOutside(event: MouseEvent) {
			const target = getComposedEventTarget(event);

			if (
				target &&
				ref.current &&
				!ref.current.contains(target as Node) &&
				!showSearchPerson &&
				!showRemovePerson
			) {
				setShowDetailView(false);
			}
		}

		// Bind the event listener
		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			// Unbind the event listener on clean up
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [ref, setShowDetailView, showSearchPerson, showRemovePerson]);

	// Get selected person from URL or default to first person
	const selectedPersonId = searchParams.get("selectedPersonId");

	const primaryPerson = useMemo(() => {
		if (selectedPersonId && trackerOrganization?.trackerPersons) {
			const selectedPerson = trackerOrganization.trackerPersons.find(
				(tp) => tp.prospect?.id === Number(selectedPersonId)
			);

			if (selectedPerson?.prospect) {
				return selectedPerson.prospect;
			}
		}

		// Fall back to first person
		return trackerOrganization?.trackerPersons?.[0]?.prospect;
	}, [trackerOrganization?.trackerPersons, selectedPersonId]);

	// Lists this person belongs to — sourced from the contacts endpoint (same prospect id), so the
	// placement widget can show where else the lead lives. Gated on the CONTACT feature to avoid
	// an unauthorized request for users without it.
	const isContactActive = useIsFeatureActive(FeaturesCodes.CONTACT);

	const { data: prospectListDetail } = useGetContactProspectDetailQuery(primaryPerson?.id ?? 0, {
		skip: !isContactActive || !primaryPerson?.id,
	});

	// Read deepResearchResponse from the Redux slice (updated by websocket)
	const { records } = useAppSelector(profileTrackerSelector);

	const sliceDeepResearchResponse = useMemo(() => {
		if (!primaryPerson?.id || !selectedTrackerOrganization?.id) {
			return undefined;
		}

		const record = records.find((r) => r.id === selectedTrackerOrganization.id);

		if (!record?.trackerPersons) {
			return undefined;
		}

		const trackerPerson = record.trackerPersons.find((tp) => tp.prospectId === primaryPerson.id);

		return trackerPerson?.prospect?.deepResearchResponse;
	}, [records, primaryPerson?.id, selectedTrackerOrganization?.id]);

	const hasPersons = useMemo(() => {
		return (trackerOrganization?.trackerPersons?.length || 0) > 0;
	}, [trackerOrganization?.trackerPersons]);

	const hasValidResearchLead = useMemo(() => {
		return hasValidDeepResearchLead(primaryPerson);
	}, [primaryPerson]);

	// Reset image errors when trackerOrganization changes
	useEffect(() => {
		setCompanyLogoError(false);
	}, [trackerOrganization?.id]);

	const profilesCount = useMemo(() => {
		return trackerOrganization?.trackerPersons?.length || 0;
	}, [trackerOrganization?.trackerPersons]);

	const researchDeepResearchResponse = useMemo(() => {
		if (!hasValidResearchLead) {
			return undefined;
		}

		return sliceDeepResearchResponse ?? primaryPerson?.deepResearchResponse;
	}, [hasValidResearchLead, sliceDeepResearchResponse, primaryPerson?.deepResearchResponse]);

	const researchCount = useMemo(() => {
		const response = researchDeepResearchResponse?.response;

		return response ? Object.keys(response).length : 0;
	}, [researchDeepResearchResponse?.response]);

	const isDeepResearchActive = useIsFeatureActive(FeaturesCodes.DEEP_RESEARCH);

	const tabs: LeadPanelTabItem<Tabs>[] = [
		{ label: ts("tabs.OVERVIEW"), value: Tabs.OVERVIEW },
		...(isDeepResearchActive
			? [{ label: ts("tabs.RESEARCH"), value: Tabs.RESEARCH, count: researchCount }]
			: []),
		{ label: ts("tabs.TIMELINE"), value: Tabs.TIMELINE },
		{ label: ts("tabs.PROFILES"), value: Tabs.PROFILES, count: profilesCount },
	];

	// The badge every email icon shows (a RISKY address reads as an email too, PREC-4265).
	const getEmailStatus = (emailStatus?: string | null): EmailIconProps["status"] =>
		getEmailIconStatus(primaryPerson?.email, emailStatus);

	const handleAddPeopleToTracker = async (linkedinUrls: string[]) => {
		if (trackerOrganization) {
			await addPeopleToTrackerOrganization({
				id: trackerOrganization.id,
				people: linkedinUrls.map((url) => ({ navigationUrl: url }) as PersonSearch),
			}).unwrap();
		}
	};

	const removePerson = async (person: TrackerPerson) => {
		setSelectedTrackerPerson(person);
		setShowRemovePerson(true);
	};

	const handleRemovePersonFromTrackerOrganization = async () => {
		await removePersonFromTrackerOrganization({
			personId: selectedTrackerPerson?.id || 0,
			id: trackerOrganization?.id || 0,
		}).unwrap();

		setSelectedTrackerPerson(undefined);
		setShowRemovePerson(false);
	};

	const handleAddProspectToTrackerOrganization = async (prospectId: number) => {
		if (!trackerOrganization) {
			return;
		}

		setTogglingProspectId(prospectId);
		try {
			await addProspectToTrackerOrganization({
				id: trackerOrganization.id,
				prospectId,
			}).unwrap();
		} finally {
			setTogglingProspectId(null);
		}
	};

	const organization = useMemo(() => {
		return trackerOrganization?.organization || trackerOrganization?.holdingOrganization;
	}, [trackerOrganization]);

	return ReactDOM.createPortal(
		<div
			ref={(node) => {
				ref.current = node;
				setPanelEl(node);
			}}
			className="absolute right-0 top-0 w-[450px] h-full z-[150] transition-opacity"
		>
			<CollapseGrabber
				ariaLabel={t("basics.collapse")}
				collapsed={false}
				edge="left"
				onToggle={() => setShowDetailView(false)}
			/>
			<div className="h-full overflow-y-auto bg-white shadow-lg text-sm">
				{isLoadingTrackerOrganization && <LoadingOverlay />}

				{/* Main content area */}
				<div className="py-6">
					{/* Person Header - only show if there are persons */}
					{hasPersons && primaryPerson && (
						<PersonHeader
							getEmailStatus={getEmailStatus}
							organization={organization}
							primaryPerson={primaryPerson}
						/>
					)}

					{/* Company Header */}
					<CompanyHeader
						companyLogoError={companyLogoError}
						organization={organization}
						setCompanyLogoError={setCompanyLogoError}
						trackerOrganizationName={trackerOrganization?.name}
					/>

					{/* Tabs */}
					<LeadPanelTabs activeTab={currentTab} tabs={tabs} onChange={setCurrentTab} />

					<div className="isolate px-4 pb-5 pt-3.5">
						{currentTab === Tabs.OVERVIEW && (
							<>
								{/* Lists this lead is on */}
								{prospectListDetail?.listPersons && prospectListDetail.listPersons.length > 0 && (
									<ContactLeadLocation
										hideWhenEmpty
										lists={prospectListDetail.lists}
										listPersons={prospectListDetail.listPersons}
										prospectName={primaryPerson?.name}
									/>
								)}
								<OverviewTab
									afterSummary={
										primaryPerson?.id ? (
											<RecentSignalsBox
												companyName={organization?.name}
												firstName={
													primaryPerson.firstName || (primaryPerson.name?.split(" ")[0] ?? "")
												}
												isTracked
												prospectId={primaryPerson.id}
												prospectName={primaryPerson.name ?? ""}
												onSeeTimeline={() => setCurrentTab(Tabs.TIMELINE)}
											/>
										) : undefined
									}
									columns={trackerOrganization?.columns || []}
									hasPersons={hasPersons}
									organization={organization}
									primaryPerson={primaryPerson}
								/>
							</>
						)}

						{currentTab === Tabs.RESEARCH && isDeepResearchActive && (
							<DeepResearchPanel
								deepResearchResponse={researchDeepResearchResponse}
								portalContainer={panelEl}
								prospectId={hasValidResearchLead ? primaryPerson?.id : undefined}
							/>
						)}

						{currentTab === Tabs.TIMELINE && (
							<SignalTimeline
								signalFilterStatus={signalFilterStatus}
								trackerOrganizationId={trackerOrganization?.id || 0}
							/>
						)}

						{currentTab === Tabs.PROFILES && (
							<ProfilesTab
								addProspectIsLoading={addProspectIsLoading}
								failedProfileImages={failedProfileImages}
								handleAddProspectToTrackerOrganization={handleAddProspectToTrackerOrganization}
								removePerson={removePerson}
								removePersonIsLoading={removePersonIsLoading}
								selectedTrackerPerson={selectedTrackerPerson}
								setFailedProfileImages={setFailedProfileImages}
								setShowSearchPerson={setShowSearchPerson}
								togglingProspectId={togglingProspectId}
								trackerOrganization={trackerOrganization}
							/>
						)}
					</div>
					{showSearchPerson && (
						<GlobalSearchModal
							confirmLabel={t("list.addPerson")}
							initialFilterCompany={
								organization?.primaryDomain
									? { name: organization.name ?? "", domain: organization.primaryDomain }
									: undefined
							}
							isOpened={showSearchPerson}
							setIsOpened={setShowSearchPerson}
							onConfirmSelection={handleAddPeopleToTracker}
						/>
					)}
					<RemovePersonModal
						errorRemoving={errorRemoving}
						handleRemovePersonFromTrackerOrganization={handleRemovePersonFromTrackerOrganization}
						removePersonIsLoading={removePersonIsLoading}
						selectedTrackerPerson={selectedTrackerPerson}
						setShowRemovePerson={setShowRemovePerson}
						showRemovePerson={showRemovePerson}
						ts={ts}
					/>
				</div>
			</div>
		</div>,
		document.body
	);
};
